/**
 * In-browser implementation of the relay, for the fully-static build.
 *
 * Fixture mode never needed a server: the load book, the recorded response and
 * the fallback rating model are all local. This module runs that same code in
 * the browser so the app can be hosted as flat files - no Worker, no KV, no
 * RateLinx token, nothing to protect. That makes it safe to leave publicly
 * reachable, which in turn makes it embeddable in an iframe (Cloudflare Access
 * would redirect a framed page to a login screen that cannot render).
 *
 * It imports the EXACT modules the Worker uses - worker/normalize.js and
 * worker/rating-model.js - so the static build and the live build cannot drift
 * in how they price or rank anything.
 *
 * Enabled at build time with VITE_STATIC=1. The network path in api.ts is
 * untouched and remains the default.
 */

import loadBook from '../../data/loads.json';
import recorded004192 from '../../worker/fixtures/LOAD-004192.rateshop.json';
import { normaliseRateShop } from '../../worker/normalize.js';
import { synthesiseRateShop } from '../../worker/rating-model.js';
import { buildShipment } from '../../worker/shipment.js';

import type { BookResult, Load, LoadList, RateShopResult, RelayConfig, Selection } from '../types';

const FIXTURES: Record<string, unknown> = {
  'LOAD-004192': recorded004192,
};

/** Mirrors the wrangler.jsonc vars, so the request payload shown in the UI is real. */
const CONFIG = {
  ratelinx: {
    baseUrl: 'https://portalapi.ratelinx.com',
    mode: 'fixture' as const,
    clientId: 'DASHBOARD',
    locationId: 403213250,
    userId: 'rsmdemo',
    programId: 'RLXAPI3',
    engine: 'LRMT',
    billingType: '1',
  },
};

// --- Selections ------------------------------------------------------------
// Per-browser rather than shared. In an iframe test that is what you want:
// two people poking at the embedded page do not collide.

const STORAGE_KEY = 'ratelinx.selections.v1';

function readSelections(): Record<string, Selection> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, Selection>) : {};
  } catch {
    // Private windows and third-party-cookie-blocked iframes both throw here.
    // Losing the selection is survivable; a crashed page is not.
    return {};
  }
}

function writeSelections(all: Record<string, Selection>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* no-op - see readSelections */
  }
}

const book = loadBook as unknown as { asOfDate: string; generatedAt: string; loads: Load[] };

function withSelection(load: Load): Load {
  const sel = readSelections()[load.loadId];
  return sel ? { ...load, selection: sel } : load;
}

function findLoad(loadId: string): Load {
  const load = book.loads.find((l) => l.loadId === loadId);
  if (!load) throw new Error(`No load "${loadId}" in the seed source`);
  return withSelection(load);
}

/** Small delay so loading states are visible rather than flashing. */
const settle = <T,>(value: T, ms = 220): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

export const staticApi = {
  async config(): Promise<RelayConfig> {
    return settle(
      {
        ratelinxMode: 'fixture',
        ratelinxRequestedMode: 'fixture',
        ratelinxBaseUrl: CONFIG.ratelinx.baseUrl,
        ratelinxEnvironment: 'test',
        hasToken: false,
        allowLiveBooking: false,
        d365Source: 'seed',
        clientId: CONFIG.ratelinx.clientId,
        locationId: CONFIG.ratelinx.locationId,
        user: null,
        selectionsPersist: true,
      },
      0,
    );
  },

  async loads(): Promise<LoadList> {
    return settle({
      source: 'seed',
      asOfDate: book.asOfDate,
      generatedAt: book.generatedAt,
      loads: book.loads.map(withSelection),
    });
  },

  async load(loadId: string): Promise<Load> {
    return settle(findLoad(loadId));
  },

  async rateShop(loadId: string): Promise<RateShopResult> {
    return this.rateShopLoad(findLoad(loadId));
  },

  /**
   * Rates a load passed by value. The embed view builds a load out of URL
   * parameters, so it has no id to look up - the rate has to come from the
   * shipment D365 actually sent.
   */
  async rateShopLoad(load: Load): Promise<RateShopResult> {
    const payload = buildShipment(load, { intent: 'rateshop' }, CONFIG);

    const fixture = FIXTURES[load.loadId];
    const rateSource = fixture ? 'recorded' : 'synthetic';
    const response = fixture ? rebase(fixture, load) : synthesiseRateShop(load);

    const normalised = normaliseRateShop(response, load) as RateShopResult;

    return settle({
      ...normalised,
      rateSource,
      elapsedMs: 0,
      warning: fixture
        ? null
        : 'Modelled rates - no recorded carrier response exists for this load.',
      requestPayload: payload,
    });
  },

  async select(loadId: string, quote: SelectInput): Promise<Load> {
    const all = readSelections();
    all[loadId] = { ...quote, booked: false, selectedAt: new Date().toISOString() };
    writeSelections(all);
    return settle(findLoad(loadId), 120);
  },

  async clearSelection(loadId: string): Promise<Load> {
    const all = readSelections();
    delete all[loadId];
    writeSelections(all);
    return settle(findLoad(loadId), 80);
  },

  async reset(): Promise<{ cleared: number; durable: boolean }> {
    const n = Object.keys(readSelections()).length;
    writeSelections({});
    return settle({ cleared: n, durable: true }, 80);
  },

  async book(loadId: string, quote: SelectInput): Promise<BookResult> {
    return this.bookLoad(findLoad(loadId), quote);
  },

  /**
   * Books a load passed by value.
   *
   * The embed view is opened for a real D365 load id (USMF-000001) that will
   * never be in the demo book, so booking must not depend on a seed lookup -
   * findLoad would throw and the Book button would only ever error.
   */
  async bookLoad(load: Load, quote: SelectInput): Promise<BookResult> {
    const loadId = load.loadId;
    const suffix = String(Math.abs(hash(`${loadId}-${quote.shipVia}`)) % 1e9).padStart(9, '0');

    const all = readSelections();
    all[loadId] = {
      ...quote,
      booked: true,
      trackingNumber: `DEMO-${suffix}`,
      shipmentId: `DEMO-SHP-${suffix.slice(0, 6)}`,
      selectedAt: new Date().toISOString(),
    };
    writeSelections(all);

    return settle({
      booking: {
        shipVia: quote.shipVia,
        serviceName: quote.serviceName,
        carrier: 'FedEx',
        trackingNumber: `DEMO-${suffix}`,
        shipmentId: `DEMO-SHP-${suffix.slice(0, 6)}`,
        bolNumber: '',
        proNumber: '',
        charge: quote.rate,
        status: 'Simulated',
        resultDesc: 'Simulated booking (static build)',
        // No label. A carrier label is a real shipping record; this kit does
        // not fabricate one. See BookingPanel.
        labels: [],
        bookedAt: new Date().toISOString(),
      },
      load: { ...load, selection: all[loadId] },
      rateSource: 'synthetic',
      elapsedMs: 0,
      warning: 'Simulated booking - nothing was tendered to a carrier.',
    });
  },
};

interface SelectInput {
  shipVia: string;
  serviceName: string;
  rate: number;
  deliveryDate: string | null;
  transitDays: number;
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h | 0;
}

/** Slides the recorded capture onto the load's current ship date. */
function rebase(fixture: unknown, load: Load): unknown {
  const f = fixture as { _recordedShipDate?: string; ResultData?: { Results?: RawResult[] } };
  if (!f._recordedShipDate) return f;

  const delta = Math.round(
    (Date.parse(`${load.shipDate}T00:00:00Z`) - Date.parse(`${f._recordedShipDate}T00:00:00Z`)) /
      86400000,
  );
  if (delta === 0) return f;

  const clone = structuredClone(f);
  for (const r of clone.ResultData?.Results ?? []) {
    r.ActDeliveryDate = shiftUsDate(r.ActDeliveryDate, delta);
  }
  return clone;
}

interface RawResult {
  ActDeliveryDate: string;
}

function shiftUsDate(value: string, days: number): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value ?? '');
  if (!m) return value;
  const d = new Date(Date.UTC(Number(m[3]), Number(m[1]) - 1, Number(m[2])));
  d.setUTCDate(d.getUTCDate() + days);
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getUTCFullYear()}`;
}
